import { useState, useRef } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { QrCode, Printer, Camera, Package } from 'lucide-react';
import { QRGenerator } from '@/components/QR/QRGenerator';
import { QRScanner } from '@/components/QR/QRScanner';
import { PrintableLabel } from '@/components/QR/PrintableLabel';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'sonner';

const QRTraceability = () => {
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('generate');
  const [showScanner, setShowScanner] = useState(false);
  const [scannedData, setScannedData] = useState<any>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // Form state for generating QR
  const [selectedWorkOrder, setSelectedWorkOrder] = useState('');
  const [selectedProcess, setSelectedProcess] = useState('');
  const [pieceNumber, setPieceNumber] = useState('');
  const [qrData, setQrData] = useState<any>(null);

  // Fetch work orders
  const { data: workOrders } = useQuery({
    queryKey: ['work-orders-qr'],
    queryFn: async () => {
      const { data, error } = await api
        .from('work_orders')
        .select('*')
        .in('status', ['pending', 'in_progress'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Fetch processes
  const { data: processes } = useQuery({
    queryKey: ['processes-qr'],
    queryFn: async () => {
      const { data, error } = await api
        .from('manufacturing_processes')
        .select('*')
        .order('sequence_order');

      if (error) throw error;
      return data;
    },
  });

  const handleGenerateQR = () => {
    if (!selectedWorkOrder || !selectedProcess || !user || !profile) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    const workOrder = workOrders?.find(wo => wo.id === selectedWorkOrder);
    const process = processes?.find(p => p.id === selectedProcess);

    if (!workOrder || !process) {
      toast.error('Error al cargar los datos');
      return;
    }

    const data = {
      workOrderId: workOrder.id,
      workOrderNumber: workOrder.order_number,
      productName: workOrder.product_name,
      processId: process.id,
      processName: process.name,
      operatorId: user.id,
      operatorName: profile.full_name,
      timestamp: new Date().toISOString(),
      pieceNumber: pieceNumber || undefined,
    };

    setQrData(data);
    toast.success('Código QR generado exitosamente');
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Etiqueta-${qrData?.workOrderNumber}-${qrData?.pieceNumber || 'SN'}`,
  });

  const handleScan = (data: any) => {
    setScannedData(data);
    setShowScanner(false);
    toast.success('Datos del código QR cargados');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <QrCode className="w-8 h-8" />
              Trazabilidad con Códigos QR
            </h1>
            <p className="text-muted-foreground mt-2">
              Generar, escanear e imprimir etiquetas QR para trazabilidad de piezas
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="generate">
              <QrCode className="w-4 h-4 mr-2" />
              Generar
            </TabsTrigger>
            <TabsTrigger value="scan">
              <Camera className="w-4 h-4 mr-2" />
              Escanear
            </TabsTrigger>
            <TabsTrigger value="history">
              <Package className="w-4 h-4 mr-2" />
              Historial
            </TabsTrigger>
          </TabsList>

          <TabsContent value="generate" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Generator Form */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Datos de la Etiqueta
                </h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="workOrder">Orden de Trabajo *</Label>
                    <Select value={selectedWorkOrder} onValueChange={setSelectedWorkOrder}>
                      <SelectTrigger id="workOrder">
                        <SelectValue placeholder="Selecciona una orden" />
                      </SelectTrigger>
                      <SelectContent>
                        {workOrders?.map((wo) => (
                          <SelectItem key={wo.id} value={wo.id}>
                            {wo.order_number} - {wo.product_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="process">Proceso *</Label>
                    <Select value={selectedProcess} onValueChange={setSelectedProcess}>
                      <SelectTrigger id="process">
                        <SelectValue placeholder="Selecciona un proceso" />
                      </SelectTrigger>
                      <SelectContent>
                        {processes?.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pieceNumber">Número de Pieza (Opcional)</Label>
                    <Input
                      id="pieceNumber"
                      type="text"
                      placeholder="Ej: P-001"
                      value={pieceNumber}
                      onChange={(e) => setPieceNumber(e.target.value)}
                    />
                  </div>

                  <Button onClick={handleGenerateQR} className="w-full">
                    <QrCode className="w-4 h-4 mr-2" />
                    Generar Código QR
                  </Button>
                </div>
              </Card>

              {/* QR Preview */}
              <div className="space-y-4">
                {qrData ? (
                  <>
                    <QRGenerator data={qrData} size={250} showLabel={true} />
                    
                    <Button onClick={handlePrint} className="w-full" variant="outline">
                      <Printer className="w-4 h-4 mr-2" />
                      Imprimir Etiqueta
                    </Button>

                    {/* Hidden printable component */}
                    <div style={{ display: 'none' }}>
                      <PrintableLabel ref={printRef} data={qrData} />
                    </div>
                  </>
                ) : (
                  <Card className="p-12 flex flex-col items-center justify-center text-center">
                    <QrCode className="w-16 h-16 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Completa el formulario para generar un código QR
                    </p>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="scan" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Scanner */}
              <div>
                {!showScanner ? (
                  <Card className="p-12 flex flex-col items-center justify-center text-center">
                    <Camera className="w-16 h-16 text-muted-foreground mb-4" />
                    <Button onClick={() => setShowScanner(true)} size="lg">
                      <Camera className="w-4 h-4 mr-2" />
                      Activar Cámara
                    </Button>
                  </Card>
                ) : (
                  <QRScanner
                    onScan={handleScan}
                    onClose={() => setShowScanner(false)}
                  />
                )}
              </div>

              {/* Scanned Data Display */}
              <div>
                {scannedData ? (
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Información Escaneada
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <Label>Orden de Trabajo</Label>
                        <p className="text-foreground font-medium">
                          {scannedData.workOrderNumber}
                        </p>
                      </div>
                      {scannedData.productName && (
                        <div>
                          <Label>Producto</Label>
                          <p className="text-foreground">{scannedData.productName}</p>
                        </div>
                      )}
                      <div>
                        <Label>Proceso</Label>
                        <p className="text-foreground">{scannedData.processName}</p>
                      </div>
                      <div>
                        <Label>Operario</Label>
                        <p className="text-foreground">{scannedData.operatorName}</p>
                      </div>
                      {scannedData.pieceNumber && (
                        <div>
                          <Label>Número de Pieza</Label>
                          <p className="text-foreground font-bold text-primary">
                            {scannedData.pieceNumber}
                          </p>
                        </div>
                      )}
                      <div>
                        <Label>Fecha y Hora</Label>
                        <p className="text-foreground text-sm">
                          {new Date(scannedData.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </Card>
                ) : (
                  <Card className="p-12 flex flex-col items-center justify-center text-center">
                    <Package className="w-16 h-16 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Escanea un código QR para ver la información
                    </p>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <Card className="p-12 flex flex-col items-center justify-center text-center">
              <Package className="w-16 h-16 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Historial de trazabilidad próximamente
              </p>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default QRTraceability;
